import { tenantType } from './analyticsWebTypes';
import { getDocument } from './getDocument';
import SafeLocalStorage from './storage/SafeLocalStorage';
import { type ProductInfoType } from './types';

const DEFAULT_DELAY = 1000 * 2; // 2 seconds
const DEFAULT_THROTTLE = 1000 * 60 * 60; // 1 hour
const DEFAULT_STORAGE_KEY = 'ui.viewed.last.sent';
const DEFAULT_MALFORMED_UIVIEWED_EVENT = 'malformed.uiviewed.last.sent';

enum SendEventDecision {
	FocusLost,
	UserOrTenantIncomplete,
	MalformedUiViewedRateLimited,
	EligibleToSend,
	UiViewedRateLimited
}

export default class UIViewedEvent {
	_blurListener: () => void;

	_delay: number;

	_focusListener: () => void;

	_getContext: any;

	_intervalId: any;

	_onEvent: any;

	_product: string;

	_productInfo: ProductInfoType;

	_safeLocalStorage: SafeLocalStorage;

	_storageKey: string;

	_malformedEventStorageKey: string;

	_throttle: number;

	constructor(productInfo: ProductInfoType, getContext: any, onEvent: any, settings: any = {}) {
		if (!productInfo) {
			throw new Error('Missing productInfo');
		}

		if (!productInfo.product) {
			throw new Error('Missing productInfo.product');
		}

		if (!getContext) {
			throw new Error('Missing getContext callback');
		}

		if (typeof getContext !== 'function') {
			throw new Error('Invalid getContext, must be function');
		}

		if (!onEvent) {
			throw new Error('Missing onEvent callback');
		}

		if (typeof onEvent !== 'function') {
			throw new Error('Invalid onEvent, must be function');
		}

		this._productInfo = productInfo;
		this._product = productInfo.product;
		this._getContext = getContext;
		this._onEvent = onEvent;
		this._delay = settings.delay || DEFAULT_DELAY;
		this._throttle = settings.throttle || DEFAULT_THROTTLE;
		this._storageKey = settings.storageKey || DEFAULT_STORAGE_KEY;
		this._malformedEventStorageKey =
			settings.malformedStorageKey || DEFAULT_MALFORMED_UIVIEWED_EVENT;
		this._safeLocalStorage = new SafeLocalStorage({
			envPrefix: this._productInfo.env,
		});

		this._focusListener = () => {
			this._startInterval();
		};

		this._blurListener = () => {
			this._stopInterval();
		};
	}

	start = (): void => {
		this._startInterval();
		this._bindEventListeners();
	};

	stop = (): void => {
		this._stopInterval();
		this._unbindEventListeners();
	};

	resetTimers = (): void => {
		if (this._intervalId) {
			this.stop();
			this.start();
		}
	};

	signalUserActive = (): void => {
		this._sendUserActiveEvents();
	}

	_bindEventListeners = (): void => {
		if (typeof window !== 'undefined') {
			window.addEventListener('focus', this._focusListener);
			window.addEventListener('blur', this._blurListener);
		}
	};

	_unbindEventListeners = (): void => {
		if (typeof window !== 'undefined') {
			window.removeEventListener('focus', this._focusListener);
			window.removeEventListener('blur', this._blurListener);
		}
	};

	_startInterval = (): void => {
		clearInterval(this._intervalId);
		this._intervalId = setInterval(
			this._handleDwellTimerTriggered,
			this._delay
		);
	};

	_stopInterval = (): void => {
		if (this._intervalId) {
			clearInterval(this._intervalId);
			this._intervalId = null;
		}
	};

	_getProductKey = (product: string, subproduct: string): string => {
		let productKey = product;
		if (subproduct) {
			productKey += `-${subproduct}`;
		}
		return productKey;
	};

	_getLastSentTimestamp = (productKey: string, tenantId: string, userId: string): any => {
		const json = this._parseLocalStorageData(this._storageKey) || {};

		const u = json[userId] || {};
		const t = u[tenantId] || {};
		const p = t[productKey] || null;

		return p;
	};

	_parseLocalStorageData = (key: string): any => {
		const data = this._safeLocalStorage.getItem(key);
		try {
			return JSON.parse(data || '');
		} catch {
			this._safeLocalStorage.removeItem(key);
			return null;
		}
	};

	_getMalformedLastSentTimestamp = (
		productKey: string,
		tenantId?: string,
		userId?: string,
	): any => {
		const json = this._parseLocalStorageData(this._malformedEventStorageKey) || {};
		const finalUserId = userId || 'NOT_SET';
		const finalTenantId = tenantId || 'NOT_SET';

		const u = json[finalUserId] || {};
		const t = u[finalTenantId] || {};
		const p = t[productKey] || null;

		return p;
	};

	_setLastSentTimestamp = (productKey: string, tenantId: string, userId: string): void => {
		const json = this._parseLocalStorageData(this._storageKey) || {};

		const u = json[userId] || {};
		const t = u[tenantId] || {};

		t[productKey] = Date.now();
		u[tenantId] = t;
		json[userId] = u;

		this._safeLocalStorage.setItem(this._storageKey, JSON.stringify(json));
	};

	_setMalformedLastSentTimestamp = (
		productKey: string,
		tenantId?: string,
		userId?: string,
	): void => {
		const json = this._parseLocalStorageData(this._malformedEventStorageKey) || {};

		const finalUserId = userId || 'NOT_SET';
		const finalTenantId = tenantId || 'NOT_SET';

		const u = json[finalUserId] || {};
		const t = u[finalTenantId] || {};

		t[productKey] = Date.now();
		u[finalTenantId] = t;
		json[finalUserId] = u;

		this._safeLocalStorage.setItem(this._malformedEventStorageKey, JSON.stringify(json));
	};

	_handleDwellTimerTriggered = (): void => {
		const document = getDocument();
		if (document && !document.hasFocus()) {
			return;
		}

		this._sendUserActiveEvents();
	}

	_sendUserActiveEvents = (): void => {
		const { embeddedProduct, ...context } = this._getContext();
		this._sendEvent(this._product, context, false);

		if (embeddedProduct) {
			this._sendEvent(embeddedProduct, context, true);
		}
	};

	_sendEvent = (product: string, context: any, isEmbeddedProduct: boolean): void => {
		const { subproduct, tenantIdType, tenantId, userId, lastScreenEvent, attributes } = context;
		const productKey = this._getProductKey(product, subproduct);

		const sendEventDecision = this._shouldSendEvent(
			productKey,
			tenantIdType,
			tenantId,
			userId
		);

		switch (sendEventDecision) {
			case SendEventDecision.EligibleToSend:
				this._setLastSentTimestamp(productKey, tenantId, userId);
				this._onEvent(
					this._createEvent(
						product,
						subproduct,
						isEmbeddedProduct,
						lastScreenEvent,
						attributes,
						'ui',
						'viewed',
					),
				);
				break;
			case SendEventDecision.UserOrTenantIncomplete:
				this._setMalformedLastSentTimestamp(productKey, tenantId, userId);
				this._onEvent(
					this._createEvent(
						product,
						subproduct,
						isEmbeddedProduct,
						lastScreenEvent,
						attributes,
						'malformed',
						'uiviewed',
					),
				);
				break;
			default:
				// Handle UiViewedRateLimited, FocusLost or MalformedUiViewedRateLimited if needed
				break;
		}
	};

	_shouldSendEvent = (
		productKey: string,
		tenantIdType: string,
		tenantId: string,
		userId: string
	): SendEventDecision => {
		// TENANT OR USER ID NOT SET
		if ((tenantIdType !== tenantType.NONE && !tenantId) || !userId) {
			const sinceLastSent =
				Date.now() - this._getMalformedLastSentTimestamp(productKey, tenantId, userId);

			if (sinceLastSent === null || sinceLastSent > this._throttle) {
				return SendEventDecision.UserOrTenantIncomplete;
			}

			return SendEventDecision.MalformedUiViewedRateLimited;
		}

		const sinceLastSent = Date.now() - this._getLastSentTimestamp(productKey, tenantId, userId);

		if (sinceLastSent === null || sinceLastSent > this._throttle) {
			return SendEventDecision.EligibleToSend;
		}

		return SendEventDecision.UiViewedRateLimited;
	};

	_createEvent = (
		product: string,
		subproduct: string,
		isEmbeddedProduct: boolean,
		lastScreenEvent: any,
		extraAttributes: any,
		actionSubject: string,
		action: string,
	): any => {
		const event: any = {
			product,
			source: 'ui',
			action,
			actionSubject,
			attributes: extraAttributes,
		};

		if (isEmbeddedProduct) {
			// Clear the subproduct and version as they don't relate to the embedded product
			event.subproduct = null;
			event.version = null;
			event.attributes = {
				...event.attributes,
				embeddedInEnv: this._productInfo.env,
				embeddedInProduct: this._productInfo.product,
				embeddedInSubproduct: subproduct,
				embeddedInVersion: this._productInfo.version,
				embeddedInOrigin: this._productInfo.origin,
				embeddedInPlatform: this._productInfo.platform,
			};
		}

		if (lastScreenEvent) {
			event.attributes = {
				...event.attributes,
				lastScreenEvent: {
					name: lastScreenEvent.name,
					attributes: lastScreenEvent.attributes,
				},
			};
		}

		return event;
	};
}
